import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";

const Vendors = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#2D0052]">
              Vendor & Subprocessor List
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                1. Introduction
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                Oye Creators ("we," "us," "our") uses a number of trusted
                third-party service providers (collectively, "Vendors") and,
                in turn, those Vendors may engage additional subprocessors. We
                publish this list for transparency—so that Brands, Creators
                and Enterprise Clients ("you") know where and how your data is
                handled.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                2. Definitions
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Vendor:</strong> Any external company or service
                  engaged directly by Oye Creators to process data or provide
                  infrastructure (hosting, email, analytics, payments etc.).
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Subprocessor:</strong> Any third party engaged by a
                  Vendor to perform part of that Vendor's services (e.g., a
                  hosting provider's CDN partner).
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                3. Scope
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                This list covers all Vendors and their known subprocessors as
                of the date above. We review and update it at least quarterly
                or whenever we add/remove a Vendor.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                4. List of Vendors & Subprocessors
              </h2>
              <div className="overflow-x-auto rounded-md border border-solid">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="font-semibold">
                        Vendor / Service Provider
                      </TableHead>
                      <TableHead className="font-semibold">
                        Category
                      </TableHead>
                      <TableHead className="font-semibold">
                        Data Processed
                      </TableHead>
                      <TableHead className="font-semibold">
                        Location(s)
                      </TableHead>
                      <TableHead className="font-semibold">
                        Known Subprocessors (examples)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">
                        Amazon Web Services (AWS)
                      </TableCell>
                      <TableCell>Cloud Hosting & Storage</TableCell>
                      <TableCell>
                        Application data, media files, backups
                      </TableCell>
                      <TableCell>India, USA, Europe</TableCell>
                      <TableCell>
                        Akamai (CDN); Verizon; Zayo (network transit)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Cloudflare
                      </TableCell>
                      <TableCell>CDN & DDoS Protection</TableCell>
                      <TableCell>Web traffic, IP addresses</TableCell>
                      <TableCell>Global</TableCell>
                      <TableCell>Various local PoP operators</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Google Analytics
                      </TableCell>
                      <TableCell>Web & App Analytics</TableCell>
                      <TableCell>Usage metrics, device identifiers</TableCell>
                      <TableCell>USA, Europe</TableCell>
                      <TableCell>
                        Google Cloud Platform (data processing)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        SendGrid (by Twilio)
                      </TableCell>
                      <TableCell>Email Delivery</TableCell>
                      <TableCell>
                        Transactional emails, email metadata
                      </TableCell>
                      <TableCell>USA</TableCell>
                      <TableCell>
                        Amazon SES (fallback email routing)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Stripe</TableCell>
                      <TableCell>Payment Processing</TableCell>
                      <TableCell>
                        Payment tokens, transaction records
                      </TableCell>
                      <TableCell>USA, EU</TableCell>
                      <TableCell>
                        Mastercard, Visa (payment networks)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">PayPal</TableCell>
                      <TableCell>Payment Processing</TableCell>
                      <TableCell>
                        Payment details, billing addresses
                      </TableCell>
                      <TableCell>USA, Europe</TableCell>
                      <TableCell>
                        Bank partners (e.g., JPMorgan Chase, Barclays)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Twilio</TableCell>
                      <TableCell>SMS & OTP Services</TableCell>
                      <TableCell>Phone numbers, SMS content</TableCell>
                      <TableCell>USA, India</TableCell>
                      <TableCell>
                        Local telco partners (e.g., Airtel, Jio)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Intercom</TableCell>
                      <TableCell>Customer Messaging</TableCell>
                      <TableCell>Support conversations, user IDs</TableCell>
                      <TableCell>USA</TableCell>
                      <TableCell>
                        Amazon AWS (hosting); SendGrid (email notifications)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Google Cloud Storage
                      </TableCell>
                      <TableCell>Media Storage</TableCell>
                      <TableCell>Video/images uploaded by Creators</TableCell>
                      <TableCell>USA, Asia</TableCell>
                      <TableCell>Google's own network partners</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Firebase Authentication
                      </TableCell>
                      <TableCell>User Auth & Identity</TableCell>
                      <TableCell>
                        Hashed credentials, login metadata
                      </TableCell>
                      <TableCell>USA</TableCell>
                      <TableCell>
                        Google Cloud Platform subprocessors
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Optimizely
                      </TableCell>
                      <TableCell>A/B Testing & Personalization</TableCell>
                      <TableCell>User behavior, experiment results</TableCell>
                      <TableCell>USA, EU</TableCell>
                      <TableCell>
                        Akamai (CDN); Cloudflare (web delivery)
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                5. Legal & Compliance
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>International & Indian Law:</strong> All data
                  transfers and processing comply with the EU GDPR (where
                  applicable), India's Information Technology Rules and DPDP
                  Act, and other relevant laws.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Data Security:</strong> Each Vendor and Subprocessor
                  is contractually bound to implement industry-standard
                  safeguards (encryption at rest/in transit, access controls,
                  regular audits).
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                6. Indemnification & Liability
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Vendor Responsibility:</strong> Vendors and their
                  subprocessors remain fully liable for any breach or misuse
                  of your data under their own terms and applicable law.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Oye Creators' Role:</strong> We exercise due
                  diligence when selecting Vendors and audit their compliance
                  periodically. However, Oye Creators and its employees shall
                  not be held liable for losses arising from a Vendor's or
                  Subprocessor's negligence or breach, unless directly caused
                  by our own failure to perform due diligence.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Your Remedies:</strong> In case of any data incident
                  attributable to a Vendor, you may seek remedy directly from
                  that Vendor under their published terms. We will, of course,
                  cooperate fully with any investigations or claims.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                7. Changes & Updates
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                We reserve the right to update this list at any time. Whenever
                feasible, we will notify registered users of material changes
                by email or in-app notification.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                8. Questions & Contacts
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                If you have any questions about our Vendor & Subprocessor
                List, or about how your data is processed, please reach out
                to:
              </p>
              <div className="mt-4 pl-4 border-l-4 border-r-0 border-t-0 border-b-0 border-solid border-[#D9008D]">
                <p className="text-base leading-relaxed text-gray-800">
                  Data Protection Officer
                  <br />
                  TTL Media Private Limited / Oye Creators
                  <br />
                  Email: privacy@oyecreators.com
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Vendors
